import React from 'react';
import {
  AhaEditorUtils,
  type AhaEditorV2Controller,
  HtmlSerializer,
  slate,
  Annotations,
  type AnnotationKey,
  History,
} from '@aha-app/aha-editor-v2';
import { randomId } from '@aha-app/mvc';
import type AssistantViewController from '../../components/AssistantView/AssistantViewController';
import type { ScopeItem } from '../../components/AssistantView/AssistantViewController';
import {
  createWhiteboardRecord,
  embeddedWhiteboardPlaceholder,
  insertEmbeddedWhiteboard,
} from 'components/aha_editor_field_v2/WhiteboardUtils';
import { SelectionScopeItem } from 'components/Assistant/components/PromptPanel/ScopeItems';

class AssistedEditor {
  selectionScopeItem: ScopeItem;
  pluginRegistered = false;

  constructor(private readonly editorController: AhaEditorV2Controller) {}

  get editor() {
    return this.editorController.editor;
  }

  get currentSelectionRange(): slate.Range | undefined {
    const range = this.editor?.selection;

    if (
      !range ||
      !slate.Range.isRange(range) ||
      slate.Range.isCollapsed(range)
    ) {
      return;
    }

    return range;
  }

  get hasSelection(): boolean {
    const selection = this.currentSelectionRange;

    if (!selection) return false;

    return !slate.Range.isCollapsed(selection);
  }

  getSelection(): string {
    const range = this.currentSelectionRange;
    if (!range || !this.editor || !this.editor.selection) {
      return '';
    }

    return AhaEditorUtils.Editor.textInRange(
      this.editor,
      this.editor.selection
    );
  }

  getEditorHtml(): string {
    if (!this.editor) return '';
    const serializer = new HtmlSerializer(this.editor, {
      enableFontSizeMark: true,
      enableFontFamilyMark: true,
    });
    return serializer.serialize();
  }

  updateEditorHtml(
    editorHtml: string,
    newHtml: string,
    oldHtml: string,
    operation:
      | 'insert_at_top'
      | 'insert_at_end'
      | 'replace_text'
      | 'replace_text_all'
      | 'replace_all'
  ): boolean {
    if (!this.editor) {
      return false;
    }

    const editor = this.editor;

    switch (operation) {
      // Handle insert operations separately since they don't require a diff/ops
      case 'insert_at_top':
        editorHtml = newHtml;
        break;
      case 'insert_at_end':
        editorHtml = newHtml;
        break;
      case 'replace_text':
        newHtml = editorHtml.replace(oldHtml, newHtml);
        if (newHtml === editorHtml) {
          return false;
        }
        editorHtml = newHtml;
        break;
      case 'replace_text_all':
        newHtml = editorHtml.replaceAll(oldHtml, newHtml);
        if (newHtml === editorHtml) {
          return false;
        }
        editorHtml = newHtml;
        break;
      case 'replace_all':
        if (newHtml === editorHtml) {
          return false;
        }
        editorHtml = newHtml;
        break;
      default:
        return false;
    }

    if (!editorHtml) {
      // Slate requires at least one empty paragraph
      editorHtml = '<p><br/></p>';
    }

    // Deserialize updated HTML to a normalized DocumentValue (children + metadata)
    const serializer = new HtmlSerializer(undefined, {
      enableFontSizeMark: true,
      enableFontFamilyMark: true,
    });
    const value = serializer.deserialize(editorHtml);

    if (operation === 'insert_at_top') {
      slate.Transforms.insertNodes(editor, value.children, { at: [0] });
      slate.Transforms.select(editor, {
        anchor: slate.Editor.start(editor, []),
        focus: slate.Editor.start(editor, []),
      });
      return true;
    }

    if (operation === 'insert_at_end') {
      // Using `at: [editor.children.length]` is more reliable than selecting end and inserting
      // because the latter can fail if the end node is a a table, in which case it may insert
      // inside the table instead of after it.
      slate.Transforms.insertNodes(editor, value.children, {
        at: [editor.children.length],
      });
      slate.Transforms.select(editor, {
        anchor: slate.Editor.end(editor, []),
        focus: slate.Editor.end(editor, []),
      });
      return true;
    }

    History.getForEditor(editor).addNewUndo(editor, () => {
      slate.Editor.withoutNormalizing(editor, () => {
        const oldAnnotations = editor.metadata?.annotations ?? {};

        // NOTE: Put current annotations in the history stack so they can be restored.
        Object.keys(oldAnnotations).forEach((key: AnnotationKey) => {
          const oldAnnotation = oldAnnotations[key];
          if (!oldAnnotation) return;

          Annotations.updateAnnotation(editor, {
            key,
            newProperties: oldAnnotation,
          });
        });

        // Remove all existing nodes.
        slate.Transforms.removeNodes(editor, {
          at: {
            anchor: slate.Editor.start(editor, []),
            focus: slate.Editor.end(editor, []),
          },
          hanging: true,
          voids: true,
          mode: 'highest',
        });

        // Insert new nodes.
        slate.Transforms.insertNodes(editor, value.children);

        const newAnnotations = value.metadata?.annotations ?? {};

        // Restore unresolved annotations.
        Object.keys(newAnnotations).forEach((key: AnnotationKey) => {
          const oldAnnotation = oldAnnotations[key];
          const newAnnotation = newAnnotations[key];
          if (!oldAnnotation || !newAnnotation) return;

          Annotations.updateAnnotation(editor, {
            key,
            newProperties: newAnnotation,
          });
        });
      });
    });

    return true;
  }

  insertWhiteboard(llmToolCallId): void {
    const placeholder = embeddedWhiteboardPlaceholder();

    slate.Editor.insertNode(this.editor, placeholder);

    void createWhiteboardRecord({
      objectType: this.editorController.props.objectType,
      objectId: this.editorController.props.objectId,
      baseURL: this.editorController.baseURL,
      getSharingToken: this.editorController.getSharingToken,
      llmToolCallId,
    }).then(res => {
      const {
        whiteboard: { id: whiteboardId },
      } = res;
      const placeholderId = placeholder.placeholder_id;
      insertEmbeddedWhiteboard({
        editor: this.editor,
        whiteboardId,
        placeholderId,
      });
    });
  }

  // Called multiple times - everytime a chat is instantiated
  attachToAssistantViewController(
    assistantViewController: AssistantViewController
  ): void {
    assistantViewController.setScopeItem('documentContent', {
      id: randomId() as RecordId,
      resolve: () => this.getEditorHtml(),
      render: null,
    });

    assistantViewController.setScopeItem('documentId', {
      id: randomId() as RecordId,
      resolve: () => {
        return `${this.editorController.props.objectType}-${this.editorController.props.objectId}`;
      },
      render: null,
    });

    const proxiedSelectionScopeItem = assistantViewController.setScopeItem(
      'editor-selection',
      {
        id: randomId() as RecordId,
        resolve: () => this.getSelection(),
        render: null,
      }
    );

    this.selectionScopeItem = proxiedSelectionScopeItem;
  }

  onSelectionUpdate() {
    if (this.editor.selection && this.selectionScopeItem) {
      const selectedText = AhaEditorUtils.Editor.textInRange(
        this.editor,
        this.editor.selection
      );

      if (!selectedText || selectedText.length === 0) {
        this.selectionScopeItem.render = null;
        return;
      }

      this.selectionScopeItem.render = () => {
        return <SelectionScopeItem content={selectedText} />;
      };
    }
  }

  registerSelectionChangePlugin(): void {
    // Plugin should be registered only once per editor
    if (this.pluginRegistered) {
      console.error('Error: plugin already registered');
      return;
    }

    this.editorController.ahaEditor.register({
      name: 'Detect selection changes for AI assistant',
      handlers: {
        onSelectionUpdate: () => this.onSelectionUpdate(),
      },
    });

    this.pluginRegistered = true;
  }
}

export default AssistedEditor;
